import { useEffect, useState } from 'react'
import { ChatCircleText } from '@phosphor-icons/react'
import { RatingStars } from './RatingStars'
import { ReviewList, type ReviewItem } from './ReviewList'
import { Button } from './ui/button'
import { fetchMyFreightForwarderReviews, replyToReview, type Review } from '../services/reviews'

export function FreightForwarderReviewsWidget() {
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(true)
  const [replyingId, setReplyingId] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function load() {
    setLoading(true)
    try {
      const items = await fetchMyFreightForwarderReviews()
      setReviews(items)
    } catch (err) {
      console.error('Failed to load reviews', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  async function handleReply(reviewId: string) {
    if (!replyText.trim()) return
    setSubmitting(true)
    try {
      await replyToReview(reviewId, replyText.trim())
      setReplyingId(null)
      setReplyText('')
      await load()
    } finally {
      setSubmitting(false)
    }
  }

  const total = reviews.length
  const avg = total > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / total : 0

  return (
    <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ChatCircleText size={20} className="text-amber-500" weight="fill" />
          <h3 className="font-semibold text-gray-900">Avaliações recebidas</h3>
        </div>
        {total > 0 && <RatingStars value={avg} size={16} showValue count={total} />}
      </div>
      <ReviewList
        items={reviews.slice(0, 10) as ReviewItem[]}
        loading={loading}
        renderReplyAction={(review) =>
          replyingId === review.id ? (
            <div className="mt-2 space-y-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Responda esta avaliação..."
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleReply(review.id)}
                  disabled={submitting || !replyText.trim()}
                >
                  {submitting ? 'Enviando...' : 'Enviar resposta'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setReplyingId(null); setReplyText('') }}
                  disabled={submitting}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setReplyingId(review.id); setReplyText('') }}
            >
              Responder
            </Button>
          )
        }
      />
    </div>
  )
}
